import { computed, DestroyRef, Injectable, inject, linkedSignal, resource } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, EMPTY, from, Subject, switchMap } from 'rxjs';
import type { SegmentId } from '../segment/segment-id';
import { Session } from '../session/session';
import type { BrandSlug } from '../shared/brand-slug';
import type { OrgId, UserId } from '../shared/ids';
import { resourceSettler } from '../shared/resource-settled';
import { type AccessibilityPrefs, DEFAULT_ACCESSIBILITY_PREFS } from './accessibility-prefs';
import { type BrandProductsPrefs, DEFAULT_BRAND_PRODUCTS_PREFS } from './brand-products-prefs';
import type { CompareChartKeyPrefs, CompareChartPrefs } from './compare-chart-prefs';
import type { MemberPreferences } from './member-preferences';
import type { MemberPreferencesDocument } from './member-preferences-document';
import { MemberPreferencesRepository } from './member-preferences-repository';
import type { PriceMonitorPrefs } from './price-monitor-prefs';

type MemberKey = { readonly userId: UserId; readonly orgId: OrgId };

type DocumentChange = MemberKey & { readonly document: MemberPreferencesDocument };

const AUTOSAVE_DEBOUNCE_MS = 500;
const NO_DOCUMENT: MemberPreferencesDocument = {};
const NO_COMPARE_CHART: CompareChartPrefs = {};
const NO_COMPARE_CHART_KEY: CompareChartKeyPrefs = {};
const NO_RECENT_COMPARISONS: readonly SegmentId[] = [];
const NO_PINNED_COMPARISONS: readonly SegmentId[] = [];
const NO_PRICE_MONITOR: PriceMonitorPrefs = {};
const NO_BRAND_SELECTION: readonly BrandSlug[] = [];
const NO_CATEGORY_COMPARISON_SELECTIONS: ReadonlyMap<SegmentId, readonly BrandSlug[]> = new Map();

function sameDocument(a: MemberPreferences, b: MemberPreferences): boolean {
  return a.userId === b.userId && a.orgId === b.orgId && a.document === b.document;
}

function defaultBrandProducts(): BrandProductsPrefs {
  return { ...DEFAULT_BRAND_PRODUCTS_PREFS, columns: [...DEFAULT_BRAND_PRODUCTS_PREFS.columns] };
}

/**
 * One member's preferences for the current org.
 *
 * The user and the current org are the resource's parameter, so the row is
 * keyed by `(user, org)` by construction: a different user, a sign-out or an
 * org change discards the previous row synchronously and loads the next one.
 * The working copy the UI edits is derived from that row; document edits are
 * autosaved only while a row is loaded, and always under the ids it belongs to.
 */
@Injectable({ providedIn: 'root' })
export class MemberPreferencesStore {
  private readonly repo = inject(MemberPreferencesRepository);
  private readonly session = inject(Session);
  private readonly destroyRef = inject(DestroyRef);

  private readonly snapshot = resource<
    MemberPreferences,
    { userId: UserId; orgId: OrgId } | undefined
  >({
    params: () => {
      const userId = this.session.userId();
      const orgId = this.session.currentOrgId();
      return userId && orgId ? { userId, orgId } : undefined;
    },
    loader: ({ params: { userId, orgId } }) => this.repo.forMember(userId, orgId),
  });

  /**
   * Resolves once the current member's row is loaded (or immediately for an
   * anonymous viewer); rejects with the Session or repository error if a load
   * failed.
   */
  readonly settled = resourceSettler(this.snapshot, { after: () => this.session.settled() });

  // Working copy: re-derived from the stored row whenever a new one arrives,
  // edited in place by the setters in between. A column patch keeps the
  // row's document, so it keeps the unsaved edits too.
  private readonly working = linkedSignal<MemberPreferences | null, MemberPreferencesDocument>({
    source: () => (this.snapshot.hasValue() ? this.snapshot.value() : null),
    computation: (snap, previous) =>
      previous && snap && previous.source && sameDocument(previous.source, snap)
        ? previous.value
        : (snap?.document ?? NO_DOCUMENT),
  });

  private readonly _changes$ = new Subject<DocumentChange>();

  readonly loaded = computed(() => this.snapshot.hasValue());
  /** True once the row has loaded, failed, or no member is signed in: the reactive `settled()`. */
  readonly isSettled = computed(() => {
    const status = this.snapshot.status();
    return this.sessionSettled() && status !== 'loading' && status !== 'reloading';
  });
  // The row resource sits idle, which counts as settled, until the Session names the member's org.
  private readonly sessionSettled = computed(
    () =>
      this.session.userId() === null ||
      this.session.orgIds() !== undefined ||
      this.session.error() !== null
  );
  /** Why the member's row or the Session's member orgs failed to load; null while they load or hold. */
  readonly error = computed<Error | null>(
    () => this.snapshot.error() ?? this.session.error() ?? null
  );

  readonly brandProducts = computed<BrandProductsPrefs>(
    () => this.working().brandProducts ?? defaultBrandProducts()
  );
  readonly accessibility = computed<AccessibilityPrefs>(
    () => this.working().accessibility ?? { ...DEFAULT_ACCESSIBILITY_PREFS }
  );
  /** Comparison chart controls shared across comparisons; empty until the user changes one. */
  readonly compareChart = computed<CompareChartPrefs>(
    () => this.working().compareChart ?? NO_COMPARE_CHART
  );

  /** Comparison ids the member opened, newest first; empty until one is recorded. */
  readonly recentComparisons = computed<readonly SegmentId[]>(
    () => this.working().recentComparisons ?? NO_RECENT_COMPARISONS
  );

  /** The comparison ids the member pinned, in pin order; empty when nothing is pinned. */
  readonly pinnedComparisons = computed<readonly SegmentId[]>(
    () => this.working().pinnedComparisons ?? NO_PINNED_COMPARISONS
  );

  /** Price monitoring controls; empty until the user changes one. */
  readonly priceMonitor = computed<PriceMonitorPrefs>(
    () => this.working().priceMonitor ?? NO_PRICE_MONITOR
  );

  /** The brand series pick on the overview; empty until the row is loaded. */
  readonly savedBrandSelection = computed<readonly BrandSlug[]>(() =>
    this.snapshot.hasValue() ? this.snapshot.value().selectedBrandSlugs : NO_BRAND_SELECTION
  );

  /** The brand series pick per category comparison; empty until the row is loaded. */
  readonly savedBrandSelectionByCategoryComparison = computed<
    ReadonlyMap<SegmentId, readonly BrandSlug[]>
  >(() =>
    this.snapshot.hasValue()
      ? this.snapshot.value().selectedBrandSlugsByCategoryComparison
      : NO_CATEGORY_COMPARISON_SELECTIONS
  );

  /** Whether the member finished onboarding; undefined until the row is loaded. */
  readonly hasOnboarded = computed<boolean | undefined>(() =>
    this.snapshot.hasValue() ? this.snapshot.value().hasOnboarded : undefined
  );

  /** Per-comparison chart state for one comparison URL key; empty when nothing is saved. */
  compareChartFor(key: string): CompareChartKeyPrefs {
    return this.working().compareChartByKey?.[key] ?? NO_COMPARE_CHART_KEY;
  }

  constructor() {
    this._changes$
      .pipe(
        debounceTime(AUTOSAVE_DEBOUNCE_MS),
        switchMap((change) =>
          from(this.repo.saveDocument(change.userId, change.orgId, change.document)).pipe(
            catchError((e) => {
              console.error('member-preferences write failed', e);
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  setBrandProducts(patch: Partial<BrandProductsPrefs>): void {
    const current = this.working();
    this.commit({
      ...current,
      brandProducts: { ...(current.brandProducts ?? defaultBrandProducts()), ...patch },
    });
  }

  setAccessibility(patch: Partial<AccessibilityPrefs>): void {
    const current = this.working();
    this.commit({
      ...current,
      accessibility: { ...(current.accessibility ?? { ...DEFAULT_ACCESSIBILITY_PREFS }), ...patch },
    });
  }

  setCompareChart(patch: Partial<CompareChartPrefs>): void {
    const current = this.working();
    this.commit({
      ...current,
      compareChart: { ...(current.compareChart ?? NO_COMPARE_CHART), ...patch },
    });
  }

  setCompareChartForKey(key: string, patch: Partial<CompareChartKeyPrefs>): void {
    const current = this.working();
    const byKey = current.compareChartByKey ?? {};
    this.commit({
      ...current,
      compareChartByKey: {
        ...byKey,
        [key]: { ...(byKey[key] ?? NO_COMPARE_CHART_KEY), ...patch },
      },
    });
  }

  /** Replaces the recent list wholesale; the caller owns its order and length. */
  setRecentComparisons(ids: readonly SegmentId[]): void {
    this.commit({ ...this.working(), recentComparisons: [...ids] });
  }

  /** Replaces the pinned list wholesale; an empty list clears every pin. */
  setPinnedComparisons(ids: readonly SegmentId[]): void {
    this.commit({ ...this.working(), pinnedComparisons: ids.length > 0 ? [...ids] : undefined });
  }

  setPriceMonitor(patch: Partial<PriceMonitorPrefs>): void {
    const current = this.working();
    this.commit({
      ...current,
      priceMonitor: { ...(current.priceMonitor ?? NO_PRICE_MONITOR), ...patch },
    });
  }

  setPriceMonitorProducts(brandSlug: string, eans: readonly string[]): void {
    const current = this.priceMonitor();
    this.setPriceMonitor({
      productsByBrand: { ...(current.productsByBrand ?? {}), [brandSlug]: [...eans] },
    });
  }

  /** Writes the overview brand pick for the loaded member; a no-op while no row is loaded. */
  async saveBrandSelection(slugs: readonly BrandSlug[]): Promise<void> {
    const key = this.loadedKey();
    if (!key) return;
    await this.repo.saveBrandSelection(key.userId, key.orgId, slugs);
    this.patchSnapshot(key, (p) => ({ ...p, selectedBrandSlugs: slugs }));
  }

  /** Writes one category comparison's brand pick for the loaded member; a no-op while no row is loaded. */
  async saveCategoryComparisonBrandSelection(
    segmentId: SegmentId,
    slugs: readonly BrandSlug[]
  ): Promise<void> {
    const key = this.loadedKey();
    if (!key) return;
    await this.repo.saveCategoryComparisonBrandSelection(key.userId, key.orgId, segmentId, slugs);
    this.patchSnapshot(key, (p) => {
      const next = new Map(p.selectedBrandSlugsByCategoryComparison);
      next.set(segmentId, slugs);
      return { ...p, selectedBrandSlugsByCategoryComparison: next };
    });
  }

  /** Marks onboarding complete for the loaded member. */
  async markOnboarded(): Promise<void> {
    const key = this.loadedKey();
    if (!key) throw new Error('MemberPreferencesStore.markOnboarded requires a signed-in member');
    await this.repo.markOnboarded(key.userId, key.orgId);
    this.patchSnapshot(key, (p) => ({ ...p, hasOnboarded: true }));
  }

  private loadedKey(): MemberKey | null {
    if (!this.snapshot.hasValue()) return null;
    const { userId, orgId } = this.snapshot.value();
    return { userId, orgId };
  }

  private patchSnapshot(
    key: MemberKey,
    patch: (prefs: MemberPreferences) => MemberPreferences
  ): void {
    // Resource.set aborts an in-flight load even when handed the current value, so a
    // write that lands after a user or org change leaves the resource untouched.
    if (!this.snapshot.hasValue()) return;
    const snap = this.snapshot.value();
    if (snap.userId !== key.userId || snap.orgId !== key.orgId) return;
    this.snapshot.set(patch(snap));
  }

  private commit(next: MemberPreferencesDocument): void {
    this.working.set(next);
    if (!this.snapshot.hasValue()) return;
    const { userId, orgId } = this.snapshot.value();
    this._changes$.next({ userId, orgId, document: next });
  }
}
